import { Escalation } from '@hakimo-ui/hakimo/types';
import {
  EscalationScope,
  isEscalationInScope,
  matchesAnchorAlarm,
  ScopedEscalation,
  selectActiveEscalation,
} from './select-escalation';

/**
 * Whether an escalation belongs to the group being viewed, rather than to a
 * sibling group at the same location.
 *
 * v3 groups own by scope. v2 and scan groups have no source group, so ownership
 * falls back to the initiating alarm. That fallback is load-bearing for scan:
 * treating a scope-less group as owning every incident at its location would
 * leave it with no siblings to show.
 */
export function isEscalationOwnedBy(
  escalation: ScopedEscalation,
  scope: EscalationScope
): boolean {
  if (scope.sourceGroupId) {
    return isEscalationInScope(escalation, scope);
  }
  return matchesAnchorAlarm(escalation, scope);
}

/**
 * Splits a location's escalations into the one this group should act on and the
 * rest, which render as read-only sibling banners. The active one comes from
 * `selectActiveEscalation`, so the result does not depend on payload order.
 *
 * Owned escalations other than the active one are not surfaced. Dedup allows
 * one active incident per (source group, alarm type), so a group holding
 * several at once means a resolved one is still in the payload.
 */
export function partitionEscalations(
  escalations: readonly Escalation[],
  scope: EscalationScope
): { active?: Escalation; siblings: Escalation[] } {
  const owned: Escalation[] = [];
  const siblings: Escalation[] = [];

  for (const escalation of escalations) {
    if (isEscalationOwnedBy(escalation, scope)) {
      owned.push(escalation);
    } else {
      siblings.push(escalation);
    }
  }

  return { active: selectActiveEscalation(owned), siblings };
}
